import { resetColumnsAndFilters } from '../actions/datagrid'
import { printerIconString } from './icons'
import { capitalizeFirstLetter, fieldGetter, localDate } from './utils'

export const fontFamily = 'Borna' // font-family used in ag-grid charts

// https://stackoverflow.com/questions/50533098/keep-filter-after-ag-grid-update
export const defaultColDef = {
	resizable: true,
	sortable: true,
	//flex: 1,
	//minWidth: 100,
	//filter: "agTextColumnFilter",
	filter: 'agMultiColumnFilter',
	enableValue: true,
	enableRowGroup: true,
	enablePivot: true,
	filterParams: {
		apply: true,
		newRowsAction: 'keep',
	},
	menuTabs: [
		'generalMenuTab',
		'filterMenuTab',
		//"columnsMenuTab",
	],
	//comparator: (a, b) => typeof a === "string" ? a.localeCompare(b) : (a > b ? 1 : (a < b ? -1 : 0)), // or accentedSort: true to GridOptions
	//columnsMenuParams: {},
	//floatingFilter: false,
	//aggFunc: "count",  // AG Grid adds all rows to aggregation automatically if this row is enabled
}

export const saveViewState = (state, viewStateKey) => localStorage.setItem(viewStateKey, JSON.stringify(state))

export const loadViewState = (viewStateKey) => {
	const savedViewState = localStorage.getItem(viewStateKey)
	return savedViewState ? JSON.parse(savedViewState) : {}
}

export const restoreViewState = (viewStateKey, initialState) => {
	localStorage.setItem(viewStateKey, JSON.stringify(initialState))
	window.location.reload()
}

// https://www.ag-grid.com/javascript-charts/api/
export const customChartThemes = {
	chartTheme: {
		baseTheme: 'ag-default', // ag-material ag-pastel ag-vivid ag-solar ag-solar-dark
		palette: {
			fills: ['#80DBF1', '#B7A0E0', '#6F42C1', '#00B8E4', '#8FE3D0', '#20C6A1', '#FFE599', '#F580C4'],
			strokes: ['#0000'],
		},
		overrides: {
			common: {
				title: {
					fontFamily: fontFamily,
					fontWeight: 500,
					color: '#003a78', // var(--heeros-blue)
				},
			},
			donut: {
				series: {
					innerRadiusOffset: -50,
					rotation: 30,
					calloutLabel: {
						fontFamily: fontFamily,
					},
				},
			},
			histogram: {
				axes: {
					number: {
						label: {
							fontFamily: fontFamily,
						},
						title: {
							fontFamily: fontFamily,
						},
					},
				},
			},
		},
	},
}

export const sideBar = {
	toolPanels: [
		{
			id: 'columns',
			labelDefault: 'Columns',
			labelKey: 'columns',
			iconKey: 'columns',
			toolPanel: 'agColumnsToolPanel',
			width: 300,
		},
		{
			id: 'filters',
			labelDefault: 'Filters',
			labelKey: 'filters',
			iconKey: 'filter',
			toolPanel: 'agFiltersToolPanel',
		},
	],
	defaultToolPanel: '',
}

export const statusBar = {
	statusPanels: [
		{
			statusPanel: 'agTotalAndFilteredRowCountComponent',
			align: 'right',
		},
	],
}

export const statusBarSelected = {
	statusPanels: [
		{
			statusPanel: 'agTotalAndFilteredRowCountComponent',
			align: 'right',
		},
		{
			statusPanel: 'agSelectedRowCountComponent',
			align: 'right',
		},
	],
}

// https://www.ag-grid.com/javascript-data-grid/grid-api/
function gridToJson({ api }, common) {
	const cols = api.getAllDisplayedColumns()

	const headerNames = {}
	const rowGroupCols = api.getRowGroupColumns()
	cols.forEach((col) => {
		const { colId } = col
		if (!Number.isNaN(Number(colId))) return

		if (colId.startsWith('ag-Grid-AutoColumn') && rowGroupCols.length > 0) {
			// Replace generic "Group" label with actual grouped column header(s)
			headerNames[colId] = rowGroupCols
				.map((c) => c.colDef?.headerName || c.colDef?.field || '')
				.filter(Boolean)
				.join(' / ')
		} else {
			headerNames[colId] = api.getDisplayNameForColumn(col, 'header') || colId
		}
	})

	const rows = []
	const isPivot = api.isPivotMode() && (api.getPivotColumns?.()?.length ?? 0) > 0

	const addRowValues = (node) => {
		if (!node.group && isPivot) return
		if (!node.data && !node.group) return

		const row = {}

		if (node.group && !isPivot) {
			// Non-pivot grouped mode: emit group key as a section header row with count
			let first = true
			for (const col of cols) {
				const { colId } = col
				if (!Number.isNaN(Number(colId))) continue
				row[colId] = first ? `${node.key ?? ''} (${node.allChildrenCount ?? 0})` : null
				first = false
			}
		} else {
			for (const col of cols) {
				const { colId } = col
				if (!Number.isNaN(Number(colId))) continue

				let value
				if (colId.startsWith('ag-Grid-AutoColumn') && node.group) {
					// Pivot mode group node: show key with count
					value = `${node.key ?? ''} (${node.allChildrenCount ?? 0})`
				} else if (colId.startsWith('ag-Grid-AutoColumn') && node.data && rowGroupCols.length > 0) {
					// Auto-group column on leaf nodes: get value from the grouped field
					const groupField = rowGroupCols[0]?.colDef?.field
					value = groupField ? node.data[groupField] : null
				} else {
					value = api.getCellValue({ colKey: colId, rowNode: node, useFormatter: true })
				}

				if (typeof value === 'string') value = value.replace(/⭐/g, '★')
				row[colId] = value
			}
		}

		// Skip rows where all values are null/undefined (empty group placeholders)
		if (Object.values(row).every((v) => v == null)) return
		rows.push(row)
	}

	api.forEachNodeAfterFilterAndSort ? api.forEachNodeAfterFilterAndSort(addRowValues) : api.forEachNode(addRowValues)

	return {
		common,
		headers: headerNames,
		list: rows,
	}
}

export const contextMenu = (t, params, setReport, reportCommon) => {
	const tableSubMenu = []

	if (params.api.getRowGroupColumns().length > 0) {
		tableSubMenu.push('expandAll', 'contractAll', 'separator')
	}

	tableSubMenu.push('autoSizeAll', {
		name: t('ResetColumns', 'Nollaa sarakkeet'),
		action: () => resetColumnsAndFilters(params),
	})

	const items = [
		//"copy",
		//"copyWithHeaders",
		//"copyWithGroupHeaders",
		//"paste",
		//"separator",
		/*
    {
      name: t("Table", "Taulukko"),
      icon: iconTableString,
      subMenu: tableSubMenu
    },
*/
	]

	if (params.api.isPivotMode() === true) {
		items.push('separator', 'pivotChart')
	}

	//items.push("separator");

	if (reportCommon) {
		items.push({
			name: t('PdfReport', 'PDF-raportti'),
			icon: printerIconString,
			action: () => setReport(gridToJson(params, reportCommon)),
		})
	}

	items.push('export')

	return items
}

// add timezone offset and format date for Excel/CSV export
const processCellCallback = (params) => {
	const d = params.value
	if (params.column.colDef.cellClass === 'dateType' && d instanceof Date) {
		return localDate(d)
	}
	return d
}

// https://www.ag-grid.com/react-data-grid/excel-export-data-types/
/**
 * @type {import("ag-grid-react").AgGridReactProps}
 */
export const gridSettings = {
	defaultExcelExportParams: {
		processCellCallback,
	},

	defaultCsvExportParams: {
		processCellCallback,
		columnSeparator: ';',
	},

	excelStyles: [
		{
			id: 'dateType', // should match colDef cellClass
			dataType: 'DateTime',
			numberFormat: {
				format: 'yyyy-mm-dd', // replaced by localized.dateFormat
			},
		},
	],
	//enableFilter: true,
	overlayLoadingTemplate: "<div class='spinner-border text-primary'></div>",
	maintainColumnOrder: true, // restore column order from saved state
	rowSelection: { checkboxes: false, enableClickSelection: true, mode: 'singleRow' }, // highlight clicked row
	accentedSort: true,
}

export const getRowId = (params) => String(params.data.id)

// rowSelection
export const singleRow = { mode: 'singleRow', checkboxes: false }
export const multiRow = { mode: 'multiRow' }
